import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const useStyles = makeStyles((theme) => ({
  root: {
    color: "#ffffff",
    backgroundColor: "#000000",
    padding: theme.spacing(4),
    borderRadius: 14,
    marginBottom: theme.spacing(8),
    width: 1100,
  },
  modalContent: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "600px",
    backgroundColor: "#1e1e1e",
    color: "#ffffff",
    padding: theme.spacing(3),
    borderRadius: "16px",
    fontFamily: "Raleway, sans-serif",
    outline: "none",
  },
  dropzone: {
    border: "2px dashed #444",
    borderRadius: "12px",
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.3s, background-color 0.3s",
    marginBottom: theme.spacing(2),
    backgroundColor: "#2a2a2a",
    "&:hover": {
      borderColor: "#FACC15",
    },
  },
  dropzoneActive: {
    borderColor: "#FACC15",
    backgroundColor: "#3a3a3a",
  },
  uploadIcon: {
    fontSize: "4rem",
    color: "#FACC15",
  },
  urlInputContainer: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  urlInput: {
    flexGrow: 1,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#444",
      },
      "&:hover fieldset": {
        borderColor: "#888",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#FACC15",
      },
      "&.Mui-error fieldset": {
        borderColor: "#f44336",
      },
    },
    "& .MuiInputBase-input": {
      color: "#ffffff",
    },
    "& .MuiFormLabel-root": {
      color: "#888",
    },
  },
  previewList: {
    marginTop: theme.spacing(2),
    maxHeight: "240px",
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#2a2a2a",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#FACC15",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#FFD700",
    },
  },
  previewItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5),
    backgroundColor: "#2c2c2c",
    borderRadius: "12px",
    marginBottom: theme.spacing(1.5),
  },
  fileIcon: {
    marginRight: theme.spacing(2),
    color: "#9e9e9e",
  },
  itemInfo: {
    flexGrow: 1,
    overflow: "hidden",
  },
  itemName: {
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  deleteButton: {
    color: "#9e9e9e",
    marginLeft: theme.spacing(1),
    "&:hover": {
      color: "#f44336",
    },
  },
  uploadButton: {
    backgroundColor: "#FACC15",
    color: "#000000",
    fontWeight: "bold",
    padding: theme.spacing(1.5, 4),
    fontFamily: "Poppins, sans-serif",
    "&:hover": {
      backgroundColor: "#FFD700",
    },
    "&:disabled": {
      backgroundColor: "#444",
      color: "#888",
    },
  },
  cancelButton: {
    marginRight: theme.spacing(2),
    color: "#ffffff",
    borderColor: "#888",
    "&:hover": {
      backgroundColor: "#333",
      borderColor: "#aaa",
    },
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  loadingBar: {
    height: "2px",
    backgroundColor: "transparent",
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#FACC15",
    },
  },
}));

const ModernUploader = ({
  getRootProps,
  getInputProps,
  isDragActive,
  files = [],
  url = "",
  onRemoveFile,
  onUrlChange,
  onUpload,
  loading,
  error,
  helperText,
  isModal,
  onCancel,
}) => {
  const classes = useStyles();
  const hasItemsToUpload = files.length > 0 || (url.trim() !== "" && !error);

  const paperClassName = isModal ? classes.modalContent : classes.root;

  return (
    <Paper className={paperClassName}>
      <Box
        {...getRootProps()}
        className={`${classes.dropzone} ${
          isDragActive ? classes.dropzoneActive : ""
        }`}
      >
        <input {...getInputProps()} />
        <CloudUploadOutlinedIcon className={classes.uploadIcon} />
        <Typography variant="h6">
          Drag & drop files, or click to browse
        </Typography>
        <Typography variant="body2" style={{ color: "#888" }}>
          PDF, DOCX, TXT supported. Max 50MB.
        </Typography>
      </Box>

      <Box className={classes.urlInputContainer}>
        <TextField
          className={classes.urlInput}
          fullWidth
          label="Or add a URL to scrape"
          variant="outlined"
          value={url}
          onChange={onUrlChange}
          error={error}
          helperText={helperText}
          disabled={loading}
        />
      </Box>

      <Box className={classes.previewList}>
        {files.map((file, index) => (
          <Box key={`${file.name}-${index}`} className={classes.previewItem}>
            <InsertDriveFileOutlinedIcon className={classes.fileIcon} />
            <Box className={classes.itemInfo}>
              <Typography className={classes.itemName}>{file.name}</Typography>
            </Box>
            <IconButton
              size="small"
              className={classes.deleteButton}
              onClick={() => onRemoveFile(index)}
              disabled={loading}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        ))}
      </Box>
      <LinearProgress
        className={classes.loadingBar}
        style={{ visibility: loading ? "visible" : "hidden" }}
      />
      <Box className={classes.actionsContainer}>
        {isModal && (
          <Button
            variant="outlined"
            className={classes.cancelButton}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          className={classes.uploadButton}
          onClick={onUpload}
          disabled={!hasItemsToUpload || loading}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </Box>
    </Paper>
  );
};

export default ModernUploader;
